import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import SelectOrType from '../components/SelectOrType';
import { COUNTRIES } from '../data/countries';
import { SCHOOLS } from '../data/schools';
import SiblingPicker from '../components/SiblingPicker';
import IdPhotoViewer from '../components/IdPhotoViewer';
import StaffPicker from '../components/StaffPicker';
import shrinkImage from '../utils/shrinkImage';
import './AdmissionForm.css';

/** Tədris illəri: cari ildən 2015-2016-ya qədər, yenidən köhnəyə doğru.
 *  Avqustdan sonra yeni tədris ili başlamış sayılır. */
const ACADEMIC_YEARS = (() => {
  const now = new Date();
  const startYear = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  const out = [];
  for (let y = startYear + 1; y >= 2015; y--) out.push(y + '-' + (y + 1));
  return out;
})();
import { useLanguage } from '../context/LanguageContext';


const PHONE_CODES = [
  { code:'+994', country:'AZ', flag:'🇦🇿' },
  { code:'+90',  country:'TR', flag:'🇹🇷' },
  { code:'+7',   country:'RU', flag:'🇷🇺' },
  { code:'+995', country:'GE', flag:'🇬🇪' },
  { code:'+98',  country:'IR', flag:'🇮🇷' },
  { code:'+1',   country:'US', flag:'🇺🇸' },
  { code:'+44',  country:'GB', flag:'🇬🇧' },
  { code:'+49',  country:'DE', flag:'🇩🇪' },
  { code:'+33',  country:'FR', flag:'🇫🇷' },
  { code:'+39',  country:'IT', flag:'🇮🇹' },
  { code:'+380', country:'UA', flag:'🇺🇦' },
  { code:'+92',  country:'PK', flag:'🇵🇰' },
  { code:'+91',  country:'IN', flag:'🇮🇳' },
  { code:'+971', country:'AE', flag:'🇦🇪' },
  { code:'+77',  country:'KZ', flag:'🇰🇿' },
  { code:'+998', country:'UZ', flag:'🇺🇿' },
];

function PhoneInput({ value, onChange, required, cls }) {
  const getCode = () => {
    const match = PHONE_CODES.find(p => value.startsWith(p.code));
    return match ? match.code : '+994';
  };
  const getNumber = () => {
    const match = PHONE_CODES.find(p => value.startsWith(p.code));
    return match ? value.slice(match.code.length).trim() : value.replace(/^\+\d+\s*/, '');
  };
  const code = getCode();
  const number = getNumber();

  const handleCodeChange = (newCode) => onChange(newCode + ' ' + number);
  const handleNumberChange = (newNum) => onChange(code + ' ' + newNum);

  return (
    <div style={{display:'flex',gap:4}}>
      <select value={code} onChange={e => handleCodeChange(e.target.value)}
        style={{width:95,padding:'8px 4px',border:'1px solid #d1d5db',borderRadius:'8px 0 0 8px',fontSize:'0.86rem',background:'#f9fafb',cursor:'pointer',flexShrink:0}}>
        {PHONE_CODES.map(p => (
          <option key={p.code} value={p.code}>{p.flag} {p.code}</option>
        ))}
      </select>
      <input className={cls} value={number} onChange={e => handleNumberChange(e.target.value)}
        required={required} placeholder="XX XXX XX XX"
        style={{borderRadius:'0 8px 8px 0',borderLeft:'none'}} />
    </div>
  );
}


/**
 * Sinif şəkilçisinin variantları — bazadakı mövcud adlara uyğun:
 *   bağça   → "Nursery (A)"   yəni " (A)"
 *   ibtidai → "1A"            yəni "A"
 *   yuxarı  → "7 i1"          yəni " i1"
 */
const GRADE_SUFFIXES = {
  kindergarten: [' (A)', ' (B)', ' (C)', ' (D)'],
  lower:        ['A', 'B', 'C', 'D'],
  upper:        [' i1', ' i2', ' i3', ' i4'],
};

/** target_grade-i əsas hissəyə və əlavə hərfə bölür ("1A" -> "1" + "A"). */
function splitGrade(full, cat) {
  const v = String(full || '');
  if (!v.trim()) return { base: '', suffix: '' };
  if (cat === 'kindergarten') {
    const m = v.match(/^(pre-nursery|nursery|reception|transition)(.*)$/i);
    if (m) return { base: m[1].toLowerCase(), suffix: m[2] };
  } else {
    const m = v.match(/^(\d{1,2})(.*)$/);
    if (m) return { base: m[1], suffix: m[2] };
  }
  return { base: '', suffix: v };
}

export default function AdmissionForm({ onBack, step: stepProp, setStep: setStepProp, editData, onSaved }) {
  const isEdit = !!editData;
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [stepLocal, setStepLocal] = useState(1);
  const step = stepProp !== undefined ? stepProp : stepLocal;
  const setStep = setStepProp || setStepLocal;
  const [category, setCategory] = useState('');
  const [targetGrade, setTargetGrade] = useState('');
  // Sinifə əlavə hərf (1A, 1B...) — YALNIZ redaktə zamanı
  const [gradeSuffix, setGradeSuffix] = useState('');

  const AGE_GROUPS = [
    { v: 'pre-nursery', l: t('admission.age23') },
    { v: 'nursery',     l: t('admission.age34') },
    { v: 'reception',   l: t('admission.age45') },
    { v: 'transition',  l: t('admission.age56') },
  ];

  const [sFirstName, setSFirstName] = useState('');
  const [sLastName, setSLastName] = useState('');
  const [sFatherName, setSFatherName] = useState('');
  const [sGender, setSGender] = useState('');
  const [sDob, setSDob] = useState('');
  const [sCitizenship, setSCitizenship] = useState(lang === 'en' ? 'Azerbaijan' : 'Azərbaycan');

  const [p1Type, setP1Type] = useState('valideyn');
  const [p1FirstName, setP1FirstName] = useState('');
  const [p1LastName, setP1LastName] = useState('');
  const [p1FatherName, setP1FatherName] = useState('');
  const [p1Phone, setP1Phone] = useState('+994 ');
  const [p1Email, setP1Email] = useState('');
  const [address, setAddress] = useState('');
  const [contractNo, setContractNo] = useState('');
  const [academicYear, setAcademicYear] = useState(ACADEMIC_YEARS[1] || '');
  const [p2Email, setP2Email] = useState('');
  const [p1Photo, setP1Photo] = useState(null);
  /* Yüklənmiş sənədə baxış pəncərəsi (yalnız redaktə rejimində) */
  const [docView, setDocView] = useState(null);
  const [shrinking, setShrinking] = useState(false);

  /* Sənəd seçiləndə şəkil yükləməzdən əvvəl kiçildilir — telefon şəkli
     8-12 MB olur və server həddini aşırdı. PDF olduğu kimi gedir. */
  const MAX_DOC_MB = 15;
  const pickPhoto = async (e, setter) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setter(null); return; }
    setShrinking(true);
    try {
      const out = await shrinkImage(file);
      if (out.size > MAX_DOC_MB * 1024 * 1024) {
        alert(t('admission.fileTooBig').replace('{mb}', MAX_DOC_MB));
        e.target.value = '';
        setter(null);
        return;
      }
      setter(out);
    } finally { setShrinking(false); }
  };

  const [showParent2, setShowParent2] = useState(false);
  const [p2FirstName, setP2FirstName] = useState('');
  const [p2LastName, setP2LastName] = useState('');
  const [p2FatherName, setP2FatherName] = useState('');
  const [p2Phone, setP2Phone] = useState('+994 ');
  const [p2Photo, setP2Photo] = useState(null);

  const [childStatus, setChildStatus] = useState('normal');
  const [hasSibling, setHasSibling] = useState(false);
  const [siblingName, setSiblingName] = useState('');
  /* Vergüllə ayrılmış id siyahısı — bir neçə bacı/qardaş bağlana bilər */
  /* Heyət övladıdırsa — valideyn əməkdaşın adı və bağlantısı */
  const [staffParentName, setStaffParentName] = useState('');
  const [staffParentId, setStaffParentId] = useState(null);
  const [siblingIds, setSiblingIds] = useState('');

  const [prevSchoolType, setPrevSchoolType] = useState('unknown');
  const [prevSchoolName, setPrevSchoolName] = useState('');
  const [prevCountry, setPrevCountry] = useState('');

  const [submitting, setSubmitting] = useState(false);

  const selectCategory = (cat) => {
    setCategory(cat);
    setTargetGrade('');
    setStep(2);
  };

  // Redaktə rejimində mövcud müraciəti formaya yüklə
  useEffect(() => {
    if (!editData) return;
    const cat = editData.category || '';
    const g = splitGrade(editData.target_grade, cat);
    setCategory(cat);
    setTargetGrade(g.base);
    setGradeSuffix(g.suffix);
    setSFirstName(editData.student_first_name || '');
    setSLastName(editData.student_last_name || '');
    setSFatherName(editData.student_father_name || '');
    setSGender(editData.student_gender || '');
    setSDob(editData.student_dob ? String(editData.student_dob).slice(0, 10) : '');
    setSCitizenship(editData.student_citizenship || 'Azərbaycan');
    setAddress(editData.address || '');
    setContractNo(editData.contract_number || '');
    setAcademicYear(editData.academic_year || '');
    setP1Type(editData.parent1_type || 'valideyn');
    setP1FirstName(editData.parent1_first_name || '');
    setP1LastName(editData.parent1_last_name || '');
    setP1FatherName(editData.parent1_father_name || '');
    setP1Phone(editData.parent1_phone || '+994 ');
    setP1Email(editData.parent1_email || '');
    if (editData.parent2_first_name || editData.parent2_phone) {
      setShowParent2(true);
      setP2FirstName(editData.parent2_first_name || '');
      setP2LastName(editData.parent2_last_name || '');
      setP2FatherName(editData.parent2_father_name || '');
      setP2Phone(editData.parent2_phone || '+994 ');
      setP2Email(editData.parent2_email || '');
    }
    setChildStatus(editData.child_status || 'normal');
    setHasSibling(!!editData.has_sibling_at_mtk);
    setSiblingName(editData.sibling_name || '');
    setStaffParentName(editData.staff_parent_name || '');
    setStaffParentId(editData.staff_parent_user_id || null);
    setSiblingIds(editData.sibling_student_ids
      || (editData.sibling_student_id ? String(editData.sibling_student_id) : ''));
    setPrevSchoolType(editData.previous_school_type || 'unknown');
    setPrevSchoolName(editData.previous_school_name || '');
    setPrevCountry(editData.previous_country || '');
    setStep(2);                       // kateqoriya seçimini keç
  }, [editData]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetGrade) { alert(t('admission.errorSelectGrade')); return; }
    if (!sGender) { alert(t('admission.errorSelectGender')); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('category', category);
      // Redaktədə sinifə əlavə hərf yazıla bilər: "1" + "A" -> "1A"
      fd.append('target_grade', targetGrade + (isEdit ? gradeSuffix : ''));
      fd.append('student_first_name', sFirstName);
      fd.append('student_last_name', sLastName);
      fd.append('student_father_name', sFatherName);
      fd.append('student_gender', sGender);
      if (sDob) fd.append('student_dob', sDob);
      fd.append('student_citizenship', sCitizenship);
      fd.append('parent1_type', p1Type);
      fd.append('parent1_first_name', p1FirstName);
      fd.append('parent1_last_name', p1LastName);
      fd.append('parent1_father_name', p1FatherName);
      fd.append('parent1_phone', p1Phone);
      fd.append('parent1_email', p1Email);
      fd.append('address', address);
      fd.append('contract_number', contractNo);
      fd.append('academic_year', academicYear);
      fd.append('parent2_email', p2Email);
      if (p1Photo) fd.append('parent1_id_photo', p1Photo);
      if (showParent2) {
        fd.append('parent2_first_name', p2FirstName);
        fd.append('parent2_last_name', p2LastName);
        fd.append('parent2_father_name', p2FatherName);
        fd.append('parent2_phone', p2Phone);
        if (p2Photo) fd.append('parent2_id_photo', p2Photo);
      }
      fd.append('child_status', childStatus);
      if (childStatus === 'staff') {
        fd.append('staff_parent_name', staffParentName);
        if (staffParentId) fd.append('staff_parent_user_id', String(staffParentId));
      }
      fd.append('has_sibling_at_mtk', hasSibling ? '1' : '0');
      if (hasSibling) {
        fd.append('sibling_name', siblingName);
        if (siblingIds) fd.append('sibling_student_ids', siblingIds);
      }
      fd.append('previous_school_type', prevSchoolType);
      if (prevSchoolName) fd.append('previous_school_name', prevSchoolName);
      if (prevCountry) fd.append('previous_country', prevCountry);

      if (isEdit) {
        if (p1Photo || (showParent2 && p2Photo)) {
          // Yeni sənəd şəkli seçilib — multipart göndərilir, köhnə fayl serverdə əvəz olunur
          await API.put('/admission/' + editData.id, fd,
            { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          const payload = {};
          fd.forEach((v, k) => { if (!(v instanceof File)) payload[k] = v; });
          await API.put('/admission/' + editData.id, payload);
        }
        if (onSaved) onSaved();
      } else {
        await API.post('/admission', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        // Qeydiyyat səhifəsinin içində açılıbsa, başqa səhifəyə keçmirik
        if (onSaved) onSaved(); else navigate('/requests');
      }
    } catch (err) {
      /* Server səbəbi `message` sahəsində göndərir; əvvəl yalnız `error`
         oxunurdu, ona görə "fayl çox böyükdür" kimi aydın izah itirdi və
         istifadəçi yalnız ümumi xəta görürdü. */
      alert(err.response?.data?.message
            || err.response?.data?.error
            || t('admission.errorGeneric'));
    } finally { setSubmitting(false); }
  };

  const goBack = () => {
    if (isEdit) { if (onSaved) onSaved(); return; }
    if (step === 2) { setStep(1); setCategory(''); }
    else onBack();
  };

  const cls = 'adm-input';

  return (
    <>
      {step === 1 && (
        <div className="adm-cats">
          {[
            { v:'kindergarten', l:t('admission.levelKindergarten'), d:t('admission.levelKindergartenDesc'),
              svg:<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="4"/><path d="M5 21v-1a7 7 0 0114 0v1"/><path d="M9 11l-1 3M15 11l1 3"/></svg> },
            { v:'lower', l:t('admission.levelLower'), d:t('admission.levelLowerDesc'),
              svg:<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/><path d="M8 7h8M8 11h6"/></svg> },
            { v:'upper', l:t('admission.levelUpper'), d:t('admission.levelUpperDesc'),
              svg:<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3L2 8l10 5 10-5-10-5z"/><path d="M2 8v6"/><path d="M6 10.5v5a6 3 0 0012 0v-5"/></svg> },
          ].map(c => (
            <div key={c.v} className="adm-cat-card" onClick={() => selectCategory(c.v)}>
              <div className="adm-cat-icon">{c.svg}</div>
              <div className="adm-cat-info">
                <div className="adm-cat-name">{c.l}</div>
                <div className="adm-cat-desc">{c.d}</div>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
            </div>
          ))}
        </div>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} className="adm-form">
          <div className="adm-section">
            <div className="adm-section-title">
              {category === 'kindergarten' ? t('admission.fieldAgeGroup') : t('admission.fieldClass')}
            </div>
            <div className="adm-grade-grid">
              {category === 'kindergarten' ? (
                AGE_GROUPS.map(g => (
                  <label key={g.v} className={'adm-grade-opt' + (targetGrade===g.v?' sel':'')}>
                    <input type="radio" style={{display:'none'}} checked={targetGrade===g.v} onChange={() => setTargetGrade(g.v)} />
                    {g.l}
                  </label>
                ))
              ) : (
                Array.from({length: category === 'lower' ? 5 : 7}, (_, i) => {
                  const n = category === 'lower' ? i + 1 : i + 6;
                  return (
                    <label key={n} className={'adm-grade-opt' + (targetGrade===String(n)?' sel':'')}>
                      <input type="radio" style={{display:'none'}} checked={targetGrade===String(n)} onChange={() => setTargetGrade(String(n))} />
                      {n}{t('admission.gradeSuffix')}
                    </label>
                  );
                })
              )}
            </div>

            {/* Sinifə əlavə hərf — yalnız redaktədə.
                Yeni müraciətdə sinif hələ bölünməyib, ona görə göstərilmir. */}
            {isEdit && (
              <div className="adm-grade-suffix">
                <label className="adm-label">{t('admission.fieldGradeSuffix')}</label>
                <div className="adm-suffix-row">
                  <select className={cls} value={gradeSuffix}
                    onChange={e => setGradeSuffix(e.target.value)}>
                    <option value="">—</option>
                    {(GRADE_SUFFIXES[category] || []).map(sx => (
                      <option key={sx} value={sx}>{sx.trim()}</option>
                    ))}
                  </select>
                  <span className="adm-grade-preview">
                    {t('admission.gradePreview')}: <b>{(targetGrade || '—') + gradeSuffix}</b>
                  </span>
                </div>              </div>            )}
          </div>

          <div className="adm-section">
            <div className="adm-section-title">{t('admission.sectionStudent')}</div>
            <div className="adm-row3">
              <div><label className="adm-label">{t('admission.fieldFirstName')}</label>
                <input className={cls} value={sFirstName} onChange={e => setSFirstName(e.target.value)} required /></div>
              <div><label className="adm-label">{t('admission.fieldLastName')}</label>
                <input className={cls} value={sLastName} onChange={e => setSLastName(e.target.value)} required /></div>
              <div><label className="adm-label">{t('admission.fieldFatherName')}</label>
                <input className={cls} value={sFatherName} onChange={e => setSFatherName(e.target.value)} required /></div>
            </div>
            <div className="adm-row3">
              <div><label className="adm-label">{t('admission.fieldGender')}</label>
                <select className={cls} value={sGender} onChange={e => setSGender(e.target.value)} required>
                  <option value="">{t('admission.genderSelect')}</option>
                  <option value="male">{t('admission.genderMale')}</option>
                  <option value="female">{t('admission.genderFemale')}</option>
                </select></div>
              <div><label className="adm-label">{t('admission.fieldBirthDate')}</label>
                <input type="date" className={cls} value={sDob} onChange={e => setSDob(e.target.value)} /></div>
              <div><label className="adm-label">{t('admission.fieldCitizenship')}</label>
                <SelectOrType className={cls} value={sCitizenship} onChange={setSCitizenship}
                  options={COUNTRIES} required
                  placeholder={t('admission.select')}
                  otherLabel={t('admission.optionOther')}
                  typePlaceholder={t('admission.typeCountry')} /></div>
            </div>
          </div>

          <div className="adm-section">
            <div className="adm-section-title">
              {t('admission.sectionParent')}
              <select className="adm-inline-select" value={p1Type} onChange={e => setP1Type(e.target.value)}>
                <option value="valideyn">{t('admission.typeParent')}</option>
                <option value="qeyyum">{t('admission.typeGuardian')}</option>
              </select>
            </div>
            <div className="adm-row3">
              <div><label className="adm-label">{t('admission.fieldFirstName')}</label>
                <input className={cls} value={p1FirstName} onChange={e => setP1FirstName(e.target.value)} required /></div>
              <div><label className="adm-label">{t('admission.fieldLastName')}</label>
                <input className={cls} value={p1LastName} onChange={e => setP1LastName(e.target.value)} required /></div>
              <div><label className="adm-label">{t('admission.fieldFatherNameOpt')}</label>
                <input className={cls} value={p1FatherName} onChange={e => setP1FatherName(e.target.value)} /></div>
            </div>
            <div className="adm-row2">
              <div><label className="adm-label">{t('admission.fieldPhone')}</label>
                <PhoneInput value={p1Phone} onChange={setP1Phone} required cls={cls} /></div>
              <div><label className="adm-label">{t('admission.fieldEmail')}</label>
                <input type="email" className={cls} value={p1Email}
                  onChange={e => setP1Email(e.target.value)} placeholder="ad@numune.com" /></div>
            </div>
            <div className="adm-row2">
              <div><label className="adm-label">{t('admission.fieldDocument')}</label>
                <input type="file" accept="image/*,application/pdf" className="adm-file"
                  onChange={e => pickPhoto(e, setP1Photo)} />
                {shrinking && <div className="adm-file-hint">{t('admission.shrinking')}</div>}
                {/* Redaktədə artıq yüklənmiş sənəd göstərilir — yeni fayl seçilsə, köhnəsini əvəz edir */}
                {isEdit && editData.parent1_id_photo && (
                  <button type="button" className="adm-doc-link"
                    onClick={() => setDocView([{ name: editData.parent1_id_photo,
                      label: t('enrollment.admDocP1'),
                      download: (editData.request_number || 'sened') + '-valideyn1' }])}>
                    🪪 {t('admission.viewUploadedDoc')}
                  </button>
                )}</div>
              <div />
            </div>

            {!showParent2 ? (
              <button type="button" className="adm-add-parent" onClick={() => setShowParent2(true)}>
                {t('admission.btnAddSecondParent')}
              </button>
            ) : (
              <div className="adm-parent2-block">
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span className="adm-label" style={{fontSize:'0.88rem',fontWeight:700}}>{t('admission.secondParent')}</span>
                  <button type="button" className="adm-remove-btn" onClick={() => { setShowParent2(false); setP2FirstName(''); setP2LastName(''); setP2FatherName(''); setP2Phone(''); setP2Photo(null); }}>{t('admission.btnRemove')}</button>
                </div>
                <div className="adm-row3">
                  <div><label className="adm-label">{t('admission.fieldFirstName').replace(' *','')}</label>
                    <input className={cls} value={p2FirstName} onChange={e => setP2FirstName(e.target.value)} /></div>
                  <div><label className="adm-label">{t('admission.fieldLastName').replace(' *','')}</label>
                    <input className={cls} value={p2LastName} onChange={e => setP2LastName(e.target.value)} /></div>
                  <div><label className="adm-label">{t('admission.fieldFatherNameOpt')}</label>
                    <input className={cls} value={p2FatherName} onChange={e => setP2FatherName(e.target.value)} /></div>
                </div>
                <div className="adm-row2">
                  <div><label className="adm-label">{t('admission.fieldPhoneOpt')}</label>
                    <PhoneInput value={p2Phone} onChange={setP2Phone} cls={cls} /></div>
                  <div><label className="adm-label">{t('admission.fieldEmailOpt')}</label>
                    <input type="email" className={cls} value={p2Email}
                      onChange={e => setP2Email(e.target.value)} placeholder="ad@numune.com" /></div>
                </div>
                <div className="adm-row2">
                  <div><label className="adm-label">{t('admission.fieldDocument')}</label>
                    <input type="file" accept="image/*,application/pdf" className="adm-file"
                      onChange={e => pickPhoto(e, setP2Photo)} />
                    {shrinking && <div className="adm-file-hint">{t('admission.shrinking')}</div>}
                    {isEdit && editData.parent2_id_photo && (
                      <button type="button" className="adm-doc-link"
                        onClick={() => setDocView([{ name: editData.parent2_id_photo,
                          label: t('enrollment.admDocP2'),
                          download: (editData.request_number || 'sened') + '-valideyn2' }])}>
                        🪪 {t('admission.viewUploadedDoc')}
                      </button>
                    )}</div>
                  <div />
                </div>
              </div>
            )}
          </div>

          <div className="adm-section">
            <div className="adm-row2">
              <div><label className="adm-label">{t('admission.fieldAddress')}</label>
                <input className={cls} value={address} onChange={e => setAddress(e.target.value)}
                  placeholder={t('admission.fieldAddressPh')} /></div>
              <div><label className="adm-label">{t('admission.fieldContractNo')}</label>
                <input className={cls} value={contractNo} onChange={e => setContractNo(e.target.value)}
                  placeholder={t('admission.fieldContractNoPh')} /></div>
            </div>
            <div className="adm-row2">
              <div><label className="adm-label">{t('admission.fieldAcademicYear')}</label>
                <select className={cls} value={academicYear} onChange={e => setAcademicYear(e.target.value)}>
                  <option value="">{t('admission.select')}</option>
                  {ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select></div>
              <div />
            </div>

          </div>

          <div className="adm-section">
            <div className="adm-section-title">{t('admission.sectionAdditional')}</div>
            <div className="adm-row2">
              <div>
                <label className="adm-label">{t('admission.fieldStatus')}</label>
                <div className="adm-status-grid">
                  {[
                    { v:'embassy', l:t('admission.statusEmbassy') },
                    { v:'shahid', l:t('admission.statusMartyrChild') },
                    { v:'staff',  l:t('admission.statusStaffChild') },
                  ].map(s => (
                    /* Düymədir, label deyil: label daxilindəki gizli radio
                       kliki geri qaytarırdı, onClick iki dəfə işləyib seçimi
                       elə həmin anda ləğv edirdi. Yenidən basanda seçim
                       götürülür — "adi" hal ayrıca variant deyil. */
                    <button key={s.v} type="button"
                      className={'adm-grade-opt' + (childStatus===s.v?' sel':'')}
                      onClick={() => setChildStatus(childStatus === s.v ? 'normal' : s.v)}>
                      {s.l}
                    </button>
                  ))}
                </div>
                {/* Heyət övladı seçiləndə valideynin adı soruşulur —
                    sistemdəki əməkdaşlar arasından təklif verilir */}
                {childStatus === 'staff' && (
                  <div style={{ marginTop: 10 }}>
                    <label className="adm-label">{t('admission.fieldStaffParent')}</label>
                    <StaffPicker className={cls} value={staffParentName}
                      userId={staffParentId}
                      onChange={(nm, id) => { setStaffParentName(nm); setStaffParentId(id); }}
                      placeholder={t('admission.staffParentPlaceholder')} />
                  </div>
                )}
              </div>
              <div>
                <label className="adm-label">{t('admission.fieldHasSibling')}</label>
                {/* Tək düymə vəziyyəti göstərirdi, seçim olduğu bilinmirdi —
                    indi hər iki cavab görünür, "Bəli" birincidir. */}
                <div className="adm-status-grid">
                  {[
                    { v: true,  l: t('admission.answerYes') },
                    { v: false, l: t('admission.answerNo')  },
                  ].map(o => (
                    <label key={String(o.v)}
                      className={'adm-grade-opt' + (hasSibling === o.v ? ' sel' : '')}>
                      <input type="radio" style={{display:'none'}}
                        checked={hasSibling === o.v}
                        onChange={() => { setHasSibling(o.v); if (!o.v) { setSiblingName(''); setSiblingIds(''); } }} />
                      {o.l}
                    </label>
                  ))}
                </div>
                {hasSibling && (
                  <div style={{ marginTop: 8 }}>
                    <SiblingPicker className={cls} value={siblingName}
                      ids={siblingIds}
                      onChange={(names, idStr) => { setSiblingName(names); setSiblingIds(idStr); }}
                      placeholder={t('admission.siblingPlaceholder')} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="adm-section">
            <div className="adm-section-title">{t('admission.sectionPrevSchool')}</div>
            <div className="adm-school-grid">
              {[
                { v:'unknown', l:t('admission.schoolUnknown') },
                { v:'state',   l:t('admission.schoolPublic') },
                { v:'private', l:t('admission.schoolPrivate') },
                { v:'foreign', l:t('admission.schoolForeign') },
              ].map(s => (
                <label key={s.v} className={'adm-grade-opt' + (prevSchoolType===s.v?' sel':'')}>
                  <input type="radio" style={{display:'none'}} checked={prevSchoolType===s.v}
                    onChange={() => { setPrevSchoolType(s.v); setPrevSchoolName(''); setPrevCountry(''); }} />
                  {s.l}
                </label>
              ))}
            </div>
            {prevSchoolType === 'private' && (
              <div style={{marginTop:8}}>
                <label className="adm-label">{t('admission.schoolNamePrivate')}</label>
                <SelectOrType className={cls} value={prevSchoolName} onChange={setPrevSchoolName}
                  options={SCHOOLS}
                  placeholder={t('admission.select')}
                  otherLabel={t('admission.optionOther')}
                  typePlaceholder={t('admission.schoolNamePlaceholder')} />
              </div>
            )}
            {prevSchoolType === 'state' && (
              <div style={{marginTop:8}}>
                <label className="adm-label">{t('admission.fieldSchoolName')}</label>
                <input className={cls} value={prevSchoolName} onChange={e => setPrevSchoolName(e.target.value)} placeholder={t('admission.schoolNamePlaceholder')} />
              </div>
            )}
            {prevSchoolType === 'foreign' && (
              <div className="adm-row2" style={{marginTop:8}}>
                <div><label className="adm-label">{t('admission.fieldCountry')}</label>
                  <SelectOrType className={cls} value={prevCountry} onChange={setPrevCountry}
                    options={COUNTRIES}
                    placeholder={t('admission.select')}
                    otherLabel={t('admission.optionOther')}
                    typePlaceholder={t('admission.typeCountry')} /></div>
                <div><label className="adm-label">{t('admission.schoolNamePrivate')}</label>
                  <input className={cls} value={prevSchoolName} onChange={e => setPrevSchoolName(e.target.value)} placeholder={t('admission.schoolNamePrivate')} /></div>
              </div>
            )}
          </div>

          <div className="nrf-actions">
            <button type="button" className="nrf-btn-cancel" onClick={goBack}>{t('admission.btnCancel')}</button>
            <button type="submit" className="nrf-btn-submit" disabled={submitting} style={{background:'#dc2626'}}>
              {submitting
                ? t(isEdit ? 'common.saving' : 'admission.btnSubmitting')
                : t(isEdit ? 'common.save'   : 'admission.btnSubmit')}
            </button>
          </div>
        </form>
      )}

      {docView && <IdPhotoViewer files={docView} onClose={() => setDocView(null)} />}
    </>
  );
}
